import type { Message } from '../types';

/** Anhang-Typ → Kurzlabel für die Konversations-Vorschau (wenn kein Text da ist). */
const ATTACHMENT_LABELS: Record<string, string> = {
  referral: '📣 Werbeanzeige',
  image: '📷 Bild',
  video: '🎥 Video',
  audio: '🎤 Sprachnachricht',
  sticker: '✨ Sticker',
  document: '📄 Dokument',
  contacts: '👤 Kontakt',
  location: '📍 Standort',
  template: '📋 Vorlage',
  link_preview: '🔗 Link',
  unsupported: 'Nicht unterstützter Anhang',
};

/**
 * Listen-Vorschautext einer Nachricht: bevorzugt den Text; fehlt er (reiner
 * Anhang wie Werbeanzeige/Bild/nicht unterstützt), ein sprechendes Anhang-Label
 * statt eines nichtssagenden „(kein Text)".
 */
export function messagePreview(msg?: Pick<Message, 'contentText' | 'attachments'> | null): string {
  const text = msg?.contentText?.trim();
  if (text) return text;
  const atts = msg?.attachments ?? [];
  // Das Referral ist Anzeigen-KONTEXT, kein eigener Inhalt: liegt ein echtes Medium/Element
  // daneben (z. B. Bild + Referral), dieses bevorzugt labeln, sonst das Referral selbst.
  const att = atts.find((a) => a.type !== 'referral') ?? atts[0];
  if (att) {
    // Referral: organischer Beitrag vs. Werbeanzeige — konsistent zum Eyebrow der Karte.
    if (att.type === 'referral' && att.sourceType === 'post') return '📣 Beitrag';
    return ATTACHMENT_LABELS[att.type] ?? '📎 Anhang';
  }
  return '(kein Text)';
}

const dtfShort = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
});
const dtfDate = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
const dtfRelDay = new Intl.RelativeTimeFormat('de-DE', { numeric: 'auto' });

export function formatTime(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  return dtfShort.format(date);
}

export function formatDate(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  return dtfDate.format(date);
}

export function formatRelative(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (Math.abs(diffMin) < 60) return dtfRelDay.format(diffMin, 'minute');
  const diffH = Math.round(diffMin / 60);
  if (Math.abs(diffH) < 24) return dtfRelDay.format(diffH, 'hour');
  const diffD = Math.round(diffH / 24);
  if (Math.abs(diffD) < 7) return dtfRelDay.format(diffD, 'day');
  return formatDate(date);
}
